// COP — envelope feed (LIVE-ONLY; no synthetic fallback).
//
// Default mode connects to the tak-gateway envelope feed at the same-origin /feed
// (caddy → :8674); each WS message is ONE CanonicalEnvelope JSON. If we see a dot,
// it is REAL — sourced from a real TAK server through the gateway. There is NO
// automatic fallback to synthetic data: if the live feed is unreachable the COP
// shows an honest "offline" state (zero dots) and quietly retries until the feed
// comes up. Synthetic data is reachable ONLY via the explicit ?mock=1 design
// sandbox (clearly labeled "EXERCISE · synthetic"), never by accident.
//
// The feed PUBLISHES onto the EnvelopeBus; capabilities subscribe through
// HostContext.data and never touch the socket directly.
//
// CONTRACT:P1-CANONICAL-ENVELOPE.1.0

import type { CanonicalEnvelope } from "../../../../packages/schema/ts/envelope.ts";
import type { EnvelopeBus } from "./envelopeBus.ts";
import { startScenario, startScenarioById, activeScenarioId, SCENARIO_REGISTRY } from "./scenario.ts";
import type { ScenarioHandle } from "./scenario.ts";
import { emitArmed } from "./emitClient.ts";

// Same-origin envelope feed through caddy (/feed → :8674): wss on the https
// origin, ws on http dev — so the HTTPS COP has no mixed-content block and the
// live gateway feed is reachable without a hardcoded backend port.
const GATEWAY_URL =
  (window.location.protocol === "https:" ? "wss://" : "ws://") +
  window.location.host +
  "/feed";
const CONNECT_TIMEOUT_MS = 2_500;
const RECONNECT_MS = 3_000;
const MOCK_TICK_MS = 1_000;

// "offline" = the live feed is unreachable; the COP holds an honest no-data state
// (zero dots) and keeps retrying. It NEVER means synthetic data. "mock" is reached
// only via the explicit ?mock=1 sandbox.
export type FeedMode = "connecting" | "live" | "offline" | "mock";

/** Minimal structural guard: enough to trust an incoming frame as an envelope. */
function isCanonicalEnvelope(x: unknown): x is CanonicalEnvelope {
  if (typeof x !== "object" || x === null) return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o["id"] === "string" &&
    o["schema"] === "scop.envelope/1.0" &&
    typeof o["kind"] === "string"
  );
}

// ── GEOMETRY SANITY GUARD (fail-closed) ────────────────────────────────────────
// Drop a live position update whose IMPLIED speed (Δposition ÷ Δtime vs the track's
// last ACCEPTED position) exceeds ESCAPE VELOCITY. This is a blunt safety net against a
// feed glitch / duplicate-emitter divergence teleporting a track across the planet
// (observed live: tak/live uids round-robining Hormuz↔Atlantic↔Med, ~5000nm per tick —
// drawing screen-spanning "laser" connectors). It is deliberately set ABOVE any real or
// near-future munition: ICBM apogee (~7 km/s ≈ 3.8 nm/s) and Mach-20 hypersonic glide
// (~6.8 km/s ≈ 3.7 nm/s) both sit UNDER escape velocity, so a legitimate fast track is
// NEVER errantly blocked — only the physically-impossible jumps are.
//
// Self-healing: the anchor's age caps at MAX_ANCHOR_AGE_MS. If we ever pin to a bad
// point (e.g. a track's first sighting was itself a ghost), the next update after the
// anchor goes stale RE-ANCHORS — so a track recovers in ≤30s rather than staying stuck.
// PRESENTATION/INTEGRITY ONLY: never a clearance gate (no PDP/KAS interaction).
const ESCAPE_VELOCITY_NM_PER_S = 6.04; // 11.186 km/s ÷ 1.852 km/nm
const MAX_ANCHOR_AGE_MS = 30_000;
const lastAcceptedPos = new Map<string, { lon: number; lat: number; tMs: number }>();
let geometryDrops = 0;

/** True iff `env`'s position is geometrically plausible vs the track's last accepted
 *  position. Non-point envelopes (no finite coordinate) always pass — there is nothing
 *  to judge. Accepts the first sighting of a track. Fail-closed on an impossible jump:
 *  returns false (the caller drops the frame, keeping the last good dot). */
function geometryPlausible(env: CanonicalEnvelope): boolean {
  const c = (env as unknown as { geometry?: { coordinates?: number[] } }).geometry?.coordinates;
  if (!Array.isArray(c) || !Number.isFinite(c[0]) || !Number.isFinite(c[1])) return true;
  const id = env.id;
  const tMs = Date.parse((env as unknown as { time?: string }).time ?? "") || Date.now();
  const prev = lastAcceptedPos.get(id);
  if (prev && tMs - prev.tMs <= MAX_ANCHOR_AGE_MS) {
    const dLat = (c[1] - prev.lat) * 60;
    const dLon = (c[0] - prev.lon) * 60 * Math.cos((((c[1] + prev.lat) / 2) * Math.PI) / 180);
    const nm = Math.hypot(dLat, dLon);
    const dt = Math.max((tMs - prev.tMs) / 1000, 1); // 1s floor: no div-by-zero, no sub-second false trip
    if (nm / dt > ESCAPE_VELOCITY_NM_PER_S) {
      geometryDrops++;
      if (geometryDrops % 100 === 1) {
        console.warn(
          `[feed] geometry guard: dropped ${geometryDrops} faster-than-escape-velocity jumps ` +
            `(latest ${id}: ${(nm / dt).toFixed(0)} nm/s)`,
        );
      }
      return false;
    }
  }
  lastAcceptedPos.set(id, { lon: c[0], lat: c[1], tMs });
  return true;
}

export interface FeedHandle {
  stop(): void;
  /** MOCK-ONLY: swap the active synthetic theater LIVE (no page reload). Stops the
   *  current scenario's publisher, invokes `onCleared` (the host wipes the engine / PDP /
   *  projection of the OLD theater — the feed owns the scenario lifecycle, the host owns
   *  the picture), THEN starts the new theater on the SAME bus → PDP → render path. Returns
   *  the now-active id, or null on a no-op (unknown id, already-active, or not in mock mode
   *  — the live path leaves this undefined, so a theater switch is meaningless off-mock).
   *  The new theater's entities are clearance-safe by construction: they ride the identical
   *  bus the boot scenario does (no PDP/KAS bypass). */
  setScenario?(id: string, onCleared: () => void): string | null;
}

/** Receive-only relay subscription for the DEMO cross-client sync (Plan B, services/demo-relay).
 *  Opens the SAME same-origin /feed WS the live path uses, publishes received envelopes onto the
 *  bus, and quietly reconnects — but NEVER touches FeedMode (the synthetic backdrop keeps its
 *  "mock"/EXERCISE label) and NEVER sends (authoring rides cap-chat / marker egress → POST /emit →
 *  the relay → here). Security is unchanged from the live read path: the receiver's PDP filters on
 *  render and KAS gates any sealed payload. */
function startSharedReceive(bus: EnvelopeBus): FeedHandle {
  let stopped = false;
  let socket: WebSocket | null = null;
  let retryTimer = 0;
  const schedule = (): void => {
    if (stopped) return;
    window.clearTimeout(retryTimer);
    retryTimer = window.setTimeout(connect, RECONNECT_MS);
  };
  function connect(): void {
    if (stopped) return;
    try {
      socket = new WebSocket(GATEWAY_URL);
    } catch {
      schedule();
      return;
    }
    socket.addEventListener("message", (ev: MessageEvent) => {
      try {
        const parsed: unknown = JSON.parse(String(ev.data));
        if (isCanonicalEnvelope(parsed) && geometryPlausible(parsed)) void bus.publish(parsed);
      } catch {
        // one malformed frame must not break the shared feed
      }
    });
    socket.addEventListener("close", schedule);
    socket.addEventListener("error", () => {
      /* close follows → schedule() */
    });
  }
  connect();
  return {
    stop(): void {
      stopped = true;
      window.clearTimeout(retryTimer);
      try {
        socket?.close();
      } catch {
        /* ignore */
      }
    },
  };
}

export function startFeed(
  bus: EnvelopeBus,
  onMode: (mode: FeedMode) => void,
): FeedHandle {
  // EXPLICIT synthetic sandbox — clearly labeled "EXERCISE · synthetic", never a silent
  // fallback. Two ways to enable it: (1) the ?mock=1 query param (force-on anywhere), or
  // (2) a DEMO build (VITE_DEMO_MOCK=1 at build time) so the deployed bare URL shows the
  // populated scenario without an incantation — the internal demo has no live TAK feed wired
  // yet, so live-only-by-default leaves the bare URL empty. Dev + real-feed builds omit the
  // flag → the live-feed-only path below (every dot real).
  const forceMock =
    import.meta.env.VITE_DEMO_MOCK === "1" ||
    new URLSearchParams(window.location.search).get("mock") === "1";
  if (forceMock) {
    const mock = startMock(bus, onMode);
    // DEMO COLLABORATION (Plan B): the synthetic backdrop is per-client, but when egress is ARMED
    // (the demo build / ?emit=1) ALSO RECEIVE other clients' AUTHORED events (markers + chat) from
    // the relay's /feed and layer them on top — two browsers share one picture. Receive-only; keeps
    // the "mock"/EXERCISE label (no FeedMode flap) and each receiver's PDP still filters on render.
    const shared = emitArmed() ? startSharedReceive(bus) : null;
    return {
      stop(): void {
        mock.stop();
        shared?.stop();
      },
    };
  }

  // LIVE-ONLY with a quiet reconnect loop. No synthetic fallback: if the feed is
  // down we go "offline" (zero dots) and retry — so every dot on the map is real.
  let stopped = false;
  let socket: WebSocket | null = null;
  let retryTimer = 0;
  let connectTimer = 0;

  const clearTimers = (): void => {
    window.clearTimeout(retryTimer);
    window.clearTimeout(connectTimer);
  };

  const scheduleReconnect = (): void => {
    if (stopped) return;
    onMode("offline");
    window.clearTimeout(retryTimer);
    retryTimer = window.setTimeout(connect, RECONNECT_MS);
  };

  function connect(): void {
    if (stopped) return;
    onMode("connecting");
    try {
      socket = new WebSocket(GATEWAY_URL);
    } catch {
      scheduleReconnect();
      return;
    }

    // If the socket doesn't open within the window, drop it → the close handler
    // schedules a retry (we surface "offline", never synthetic).
    window.clearTimeout(connectTimer);
    connectTimer = window.setTimeout(() => {
      if (socket && socket.readyState !== WebSocket.OPEN) {
        try {
          socket.close();
        } catch {
          /* ignore */
        }
      }
    }, CONNECT_TIMEOUT_MS);

    socket.addEventListener("open", () => {
      window.clearTimeout(connectTimer);
      onMode("live");
    });

    socket.addEventListener("message", (ev: MessageEvent) => {
      try {
        const parsed: unknown = JSON.parse(String(ev.data));
        if (isCanonicalEnvelope(parsed) && geometryPlausible(parsed)) void bus.publish(parsed);
      } catch {
        // Malformed frame: drop it. One bad message must not break the feed.
      }
    });

    // An errored socket fires close right after; do reconnect bookkeeping there once.
    socket.addEventListener("error", () => {
      /* close follows */
    });
    socket.addEventListener("close", () => {
      window.clearTimeout(connectTimer);
      scheduleReconnect();
    });
  }

  connect();

  return {
    stop() {
      stopped = true;
      clearTimers();
      try {
        socket?.close();
      } catch {
        /* ignore */
      }
    },
  };
}

// --- Synthetic sandbox (?mock=1 ONLY) --------------------------------------

/** EXPLICIT synthetic mode (?mock=1): the rich multi-INT Hormuz picture published
 *  client-side (../scenarios/hormuz.ts) for design/iteration. Labeled "EXERCISE ·
 *  synthetic" in the edition cartouche so it can never be mistaken for live data.
 *  This is NOT a fallback — startFeed reaches it only when ?mock=1 is present. */
function startMock(bus: EnvelopeBus, onMode: (m: FeedMode) => void): FeedHandle {
  onMode("mock");
  // The boot theater is chosen by the ?scenario= URL param (startScenario); the live
  // switch (setScenario below) swaps it in place. A SINGLE scenario handle is alive at
  // any time — every switch stops the old one before starting the new (no double-run).
  let scenario: ScenarioHandle = startScenario(bus, MOCK_TICK_MS);
  let activeId = activeScenarioId();
  // Reentrancy / double-start guard: a second switch that arrives mid-swap is ignored
  // (the synchronous swap below makes true reentrancy impossible, but this is cheap).
  let switching = false;
  return {
    stop() {
      scenario.stop();
    },
    setScenario(id: string, onCleared: () => void): string | null {
      if (switching) return null;
      if (!(id in SCENARIO_REGISTRY)) return null; // unknown id — no-op (keep current)
      if (id === activeId) return null; // already active — no-op (don't restart/flash)
      switching = true;
      try {
        scenario.stop(); // 1. stop the OLD theater's publisher (no more old-theater ticks)
        onCleared(); // 2. host wipes the engine / PDP / projection of the old theater
        scenario = startScenarioById(bus, MOCK_TICK_MS, id); // 3. start the new theater
        activeId = id;
        return id;
      } finally {
        switching = false;
      }
    },
  };
}
